"use client";

import { useEffect, useRef } from "react";

/**
 * Fraction of the element that must be on screen to count as seen. Half a card
 * is enough to read its image and price; requiring more would under-count the
 * bottom row on short viewports, which is exactly the population we care about.
 */
const VISIBILITY_THRESHOLD = 0.5;

/**
 * How long it must stay that visible. Without a dwell time, flinging past the
 * whole page registers an impression on every card and the click-through rate
 * this metric exists to compute collapses toward zero for everyone.
 */
const DWELL_MS = 300;

/**
 * ONE observer shared by every card on the page. Per-card observers would mean
 * ~15 of them on the homepage, each with its own callback churn on scroll.
 */
/**
 * How many extra dwell periods to wait for an element that is in view
 * geometrically but still transparent. Covers the control arm's scroll-reveal,
 * whose per-category `transitionDelay` reaches ~800ms on a five-category page.
 */
const MAX_OPACITY_RETRIES = 8;

let observer: IntersectionObserver | null = null;
const callbacks = new Map<Element, () => void>();
const pendingTimers = new Map<Element, ReturnType<typeof setTimeout>>();
const opacityRetries = new Map<Element, number>();

/**
 * Is this element actually *visible*, not merely in the viewport?
 *
 * IntersectionObserver is geometry-only: it happily reports an element that an
 * ancestor has set to `opacity: 0`. That matters here because the CONTROL arm
 * fades each category in on scroll with a staggered delay, while the challenger
 * (where the wrappers are `display: contents`) has no such wrapper at all. Left
 * unchecked, the control would log impressions for cards nobody had seen yet —
 * inflating its denominator, deflating its click-through, and biasing the one
 * metric this instrumentation exists to produce in favour of the challenger.
 *
 * `checkVisibility` is the only API that accounts for ANCESTOR opacity. Where it
 * is missing, assume visible: under-counting silently would be worse than the
 * bias it guards against.
 */
function isPerceptiblyVisible(element: Element): boolean {
  const el = element as Element & {
    checkVisibility?: (options?: {
      opacityProperty?: boolean;
      visibilityProperty?: boolean;
    }) => boolean;
  };
  if (typeof el.checkVisibility !== "function") return true;
  return el.checkVisibility({ opacityProperty: true, visibilityProperty: true });
}

function clearPending(element: Element): void {
  const timer = pendingTimers.get(element);
  if (timer !== undefined) {
    clearTimeout(timer);
    pendingTimers.delete(element);
  }
}

function stopObserving(element: Element): void {
  clearPending(element);
  callbacks.delete(element);
  opacityRetries.delete(element);
  observer?.unobserve(element);
}

/** Arm (or re-arm) the dwell timer for an element that is currently in view. */
function scheduleImpression(element: Element): void {
  pendingTimers.set(
    element,
    setTimeout(() => {
      pendingTimers.delete(element);

      if (!isPerceptiblyVisible(element)) {
        const attempts = (opacityRetries.get(element) ?? 0) + 1;
        if (attempts <= MAX_OPACITY_RETRIES) {
          // Still fading in. Wait rather than count it, and rather than drop it.
          opacityRetries.set(element, attempts);
          scheduleImpression(element);
          return;
        }
        // Persistently invisible: something other than the reveal animation is
        // hiding it. Do not count what was never shown.
        stopObserving(element);
        return;
      }

      const fire = callbacks.get(element);
      // Unobserve BEFORE firing so an impression is counted at most once per
      // page view, even if the handler itself triggers layout.
      stopObserving(element);
      fire?.();
    }, DWELL_MS),
  );
}

function handleEntries(entries: IntersectionObserverEntry[]): void {
  for (const entry of entries) {
    const element = entry.target;
    if (!entry.isIntersecting || entry.intersectionRatio < VISIBILITY_THRESHOLD) {
      // Scrolled back out before the dwell elapsed — it was never really seen.
      clearPending(element);
      continue;
    }
    if (pendingTimers.has(element)) continue;

    scheduleImpression(element);
  }
}

function getObserver(): IntersectionObserver | null {
  if (typeof IntersectionObserver === "undefined") return null;
  if (!observer) {
    observer = new IntersectionObserver(handleEntries, {
      threshold: [VISIBILITY_THRESHOLD],
    });
  }
  return observer;
}

/**
 * Fire `onImpression` once, the first time the referenced element has been at
 * least half visible for an uninterrupted {@link DWELL_MS}.
 *
 * This is the denominator the homepage has never had: `cake_card_clicked` on
 * its own cannot tell "nobody wanted this cake" apart from "nobody ever saw
 * it", which is the whole question the rail-position analysis turns on.
 *
 * `onImpression` is read through a ref, so an inline arrow function at the call
 * site does not re-register the observer on every render.
 */
export function useImpression(
  ref: React.RefObject<Element | null>,
  onImpression: () => void,
  enabled: boolean = true,
): void {
  const handlerRef = useRef(onImpression);
  handlerRef.current = onImpression;

  useEffect(() => {
    if (!enabled) return;
    const element = ref.current;
    if (!element) return;

    const io = getObserver();
    if (!io) {
      // No IntersectionObserver (very old browser, or a test environment that
      // did not polyfill it). Counting the card as seen immediately would
      // inflate impressions; reporting nothing is the honest failure mode.
      return;
    }

    callbacks.set(element, () => handlerRef.current());
    io.observe(element);
    return () => stopObserving(element);
  }, [ref, enabled]);
}

/** Test-only: drop all shared observer state between cases. */
export function __resetImpressionObserverForTests(): void {
  for (const timer of pendingTimers.values()) clearTimeout(timer);
  pendingTimers.clear();
  callbacks.clear();
  opacityRetries.clear();
  observer?.disconnect();
  observer = null;
}
